import type { ResolvedSalesOrder } from "../types/sales";
import { orderLineKey } from "utils/orderResolveLinePricing";

export type SalesViewLineTax = { amount: number; rate?: number };

export type SyncSalesViewStateResult = {
  shippingRows: Record<string, unknown>[];
  specialFeeRows: Record<string, unknown>[];
  taxByLineId: Record<string, SalesViewLineTax>;
};

/**
 * Map resolved sales order → view UI state (shipping/fees rows + per-line tax map).
 */
export function syncSalesViewStateFromResolved(
  resolved: ResolvedSalesOrder,
  currentShippingRows: unknown[] = [],
  currentFeeRows: unknown[] = []
): SyncSalesViewStateResult {
  const charges = resolved.order_charges?.[0];
  const resolvedShip = charges?.shipping_option ?? [];
  const resolvedFees = charges?.specialfee ?? [];

  const shippingRows = resolvedShip.length
    ? resolvedShip.map((r, idx) => {
        const cur = (currentShippingRows[idx] ?? currentShippingRows[0]) as Record<string, unknown>;
        const id = String(r.shipping_id ?? cur?.shipping_id ?? cur?.shipping__id ?? "");
        return {
          ...cur,
          shipping__id: id,
          shipping_id: id,
          shipping_name: String(r.shipping_name ?? cur?.shipping_name ?? ""),
          shipping_type: String(r.shipping_type ?? cur?.shipping_type ?? ""),
          shipping_amount: Number(r.shipping_amount ?? 0),
          shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
          shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
          shipping_taxable: r.shipping_taxable !== false,
          is_customer_pickup: r.is_customer_pickup === true,
        };
      })
    : (currentShippingRows as Record<string, unknown>[]);

  const specialFeeRows = resolvedFees.length
    ? resolvedFees.map((f, idx) => {
        const cur = (currentFeeRows[idx] ?? {}) as Record<string, unknown>;
        return {
          ...cur,
          specialfee_id: String(f.specialfee_id ?? cur?.specialfee_id ?? ""),
          specialfee_name: String(f.specialfee_name ?? cur?.specialfee_name ?? ""),
          specialfee_amount: Number(f.specialfee_amount ?? 0),
          specialfee_tax_amount: Number(f.specialfee_tax_amount ?? 0),
          specialfee_tax_percentage: Number(f.specialfee_tax_percentage ?? 0),
          specialfee_taxable: f.specialfee_taxable !== false,
        };
      })
    : (currentFeeRows as Record<string, unknown>[]);

  const taxByLineId: Record<string, SalesViewLineTax> = {};
  for (const it of resolved.items || []) {
    const taxAmt = Number(it.item_tax_info?.total_product_tax_amount ?? 0);
    const rate = Number(it.item_tax_info?.tax_percentage ?? 0) / 100;
    const keys = [
      String(it.sku ?? ""),
      orderLineKey(String(it.product_id ?? ""), String(it.variant_id ?? "")),
      String(it.variant_id ?? ""),
      String(it.product_id ?? ""),
    ].filter(Boolean);
    for (const k of keys) {
      taxByLineId[k] = { amount: taxAmt, rate };
    }
  }

  return { shippingRows, specialFeeRows, taxByLineId };
}
